import React, {useMemo, useState} from 'react';
import {Box} from '@mui/material';
import {useTranslation} from 'react-i18next';
import SelectSearch from "@ui/select/selectSearch/SelectSearch";
import {ISelectCustomItem} from "@ui/select/selectCustom/types";
import {IPointTemplatesSingleVoyage} from "@containers/singleVoyage/pointTemplates/types";
import {
	useGetPointTemplatesSingleVoyageQuery
} from "@containers/singleVoyage/pointTemplates/queryHooks/usePointTemplatesSingleVoyageQuery";
import {
	buildPointTemplateOptions,
	IPointTemplateOption
} from "@containers/singleVoyage/pointTemplates/helpers/buildPointTemplateOptions";
import PointTemplateOptionRow
	from "@containers/singleVoyage/pointTemplates/select/selectPointTemplate/optionRow/PointTemplateOptionRow";

interface IProps {
	accountId: number
	onChange: (value: string, pointTemplate: IPointTemplatesSingleVoyage) => void
	label?: string
	/** usually the last stop already added, so the nearest templates come first */
	origin?: { lat: number, lng: number } | null
	onHoverPointTemplate?: (pointTemplate: IPointTemplatesSingleVoyage | null) => void
}

const sxOption = {width: '100%', minWidth: 0}

const SelectPointTemplateSearch = ({
									   accountId,
									   onChange,
									   label,
									   origin,
									   onHoverPointTemplate,
								   }: IProps) => {

	const {t} = useTranslation()

	const [value, setValue] = useState<string>('')

	const {data, isLoading} = useGetPointTemplatesSingleVoyageQuery({accountId})

	const options: IPointTemplateOption[] = useMemo(() => {
		return buildPointTemplateOptions({pointTemplates: data, origin})
	}, [data, origin?.lat, origin?.lng])

	const dataSelect: ISelectCustomItem<IPointTemplateOption>[] = useMemo(() => {
		return options.map((option) => ({
			value: option.pointTemplate.id.toString(),
			label: option.pointTemplate.name,
			fullData: option,
		}))
	}, [options])

	const handleChange = (nextValue: string, option: IPointTemplateOption) => {
		if (!option?.pointTemplate) {
			return
		}
		setValue(nextValue)
		onChange(nextValue, option.pointTemplate)
		onHoverPointTemplate && onHoverPointTemplate(null)
		// the picker stays empty so the next stop can be added right away
		setTimeout(() => setValue(''), 300)
	}

	return (
		<SelectSearch
			value={value}
			onChange={handleChange}
			data={dataSelect}
			label={label || t('singleVoyage.searchPoints')}
			isLoading={isLoading}
			getSearchText={(item) => item.fullData?.pointTemplate?.address || ''}
			renderOptionContent={(item) => {
				const option = item.fullData
				if (!option?.pointTemplate) {
					return item.label
				}
				return (
					<Box
						onMouseEnter={() => onHoverPointTemplate && onHoverPointTemplate(option.pointTemplate)}
						onMouseLeave={() => onHoverPointTemplate && onHoverPointTemplate(null)}
						sx={sxOption}
					>
						<PointTemplateOptionRow
							pointTemplate={option.pointTemplate}
							distance={option.distance}
						/>
					</Box>
				)
			}}
		/>
	);
};

export default SelectPointTemplateSearch;
